"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader, Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState, use } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import apiClient from "@/lib/api";
import { createClient } from "@/lib/supabase/client";
import { useTranslation } from "@/lib/use-translations";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

export default function Signin(props) {
  const params = use(props.params);
  const { t } = useTranslation();
  const supabase = createClient();
  const router = useRouter();
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  const formSchema = z.object({
    email: z.string().email({
      message: t("public_signin:validation_email_required"),
    }),
    password: z.string().min(1, {
      message: t("public_signin:validation_password_required"),
    }),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    const checkSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session) {
          // Keep the loader visible during the redirect so the sign-in form
          // never flashes for already-authenticated users on slow connections.
          router.push("/dashboard");
          return;
        }
      } catch (error) {
        console.error("Session check error:", error);
      }
      setIsCheckingSession(false);
    };

    checkSession();
  }, [router]);

  // Show a loader while checking the session (and during the redirect for
  // already-signed-in users) instead of flashing the sign-in form.
  if (isCheckingSession) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-zinc-100">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </main>
    );
  }

  const { isSubmitting, errors } = form.formState;

  const onSubmit = async (values) => {
    try {
      const response = await apiClient.post("/auth/signin", {
        email: values.email,
        password: values.password,
      });

      if (response.success && response.redirectUrl) {
        window.location.href = response.redirectUrl;
      }
    } catch (error) {
      console.error("Signin error:", error);
      form.setError("root", { message: error.message });
    }
  };

  return (
    <main className="flex min-h-screen text-neutral-500">
      {/* Left column with image (hidden on mobile) */}
      <div className="hidden md:block md:w-1/2 relative">
        <Image
          src="/images/signup-left-5.jpg"
          alt="Signin background"
          layout="fill"
          objectFit="cover"
        />
      </div>

      {/* Right column with form */}
      <div className="w-full md:w-1/2 p-8 md:p-24 flex flex-col justify-between relative">
        <div className="max-w-[600px] mx-auto w-full">
          <Image
            src="/images/logo-popupshop.png"
            alt="PopupShop Logo"
            width={100}
            height={150}
            className="mx-auto mb-5"
          />

          <div className="bg-zinc-100 rounded-2xl px-16 py-9">
            <h1 className="text-4xl uppercase font-semibold tracking-tight mb-4 text-zinc-800">
              {t("public_signin:title")}
            </h1>
            <h2 className="text-lg font-normal tracking-tight text-neutral-500 mb-3">
              {t("public_signin:subtitle")}
            </h2>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-3"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          data-test="email-input"
                          placeholder={t("public_signin:email_placeholder")}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage data-test="email-error" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          data-test="password-input"
                          type="password"
                          placeholder={t("public_signin:password_placeholder")}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage data-test="password-error" />
                    </FormItem>
                  )}
                />

                <Button
                  data-test="submit-button"
                  type="submit"
                  variant="print-red"
                  disabled={isSubmitting}
                  className=" w-full"
                >
                  {isSubmitting && (
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("public_signin:submit_button")}
                </Button>
              </form>
            </Form>

            <p className="text-center mt-8 mb-8 text-sm font-medium">
              {t("public_signin:no_account_text")}{" "}
              <Link
                href={`/language/${params.locale}/signup`}
                className="text-primary"
              >
                {t("public_signin:signup_link")}
              </Link>
            </p>

            {errors.root && (
              <Alert data-test="root-error">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{errors.root.message}</AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        {/* Print.com logo in the footer */}
        <div className="absolute bottom-4 right-4 z-20">
          <Image
            src="/images/print.com.png"
            alt="Print.com Logo"
            width={75}
            height={30}
          />
        </div>
      </div>
    </main>
  );
}
